import { useState } from "react";
import { Typography, Spin } from "antd";
import { Navigate, useLocation } from "react-router-dom";
import { useFlags, useFeaturesListQuery } from "@/shared/lib/flags";
import { useCoordinator } from "@/shared/lib/hooks";
import { routes } from "@/shared/lib/react-router";
import { Breadcrumbs } from "@/shared/ui";
import { isUnifiedRoute } from "@shared/utils";
import { useIntegrationCards } from "../../model/hooks/use-integration-cards";
import {
  mockIntegrationCards,
  supervisionOrgans,
} from "../../model/mocks/integration-cards";
import { ConfirmIntegrationModal } from "../confirm-integration-modal";
import { DisconnectIntegrationModal } from "../disconnect-integration-modal";
import { SupervisionIntegrationCard } from "../supervision-integration-card";
import "./supervision-integration-page.scss";

export const SupervisionIntegrationPage = () => {
  const { isLoading: isFlagsLoading } = useFeaturesListQuery();
  const isIntegrationSettingsEnabled = useFlags(
    "supervisionIntegrationSettingsEnabled",
  );
  const { projectId, supervisionId } = useCoordinator();
  const location = useLocation();
  const isUnified = isUnifiedRoute(location.pathname);
  const [confirmModalState, setConfirmModalState] = useState<{
    open: boolean;
    cardId: string | null;
    organName?: string;
  }>({ open: false, cardId: null });

  const [disconnectModalState, setDisconnectModalState] = useState<{
    open: boolean;
    cardId: string | null;
    organName?: string;
  }>({ open: false, cardId: null });

  const {
    cards,
    isLoading,
    connectCard,
    saveCard,
    disconnectCard,
    enableEditMode,
    copyId,
    updateCard,
  } = useIntegrationCards(mockIntegrationCards);

  const breadcrumbs = [
    { path: routes.supervision.root(projectId), label: "Надзороне дело" },
    {
      path: routes.supervision.notifications(projectId, supervisionId),
      label: "«Бизнес-центр Невский»",
    },
    {
      path: routes.supervision.integrationSettings(projectId, supervisionId),
      label: "Настройки интеграции",
    },
  ];

  const handleConnectClick = (cardId: string) => {
    const card = cards.find((c) => c.id === cardId);
    const organName =
      card?.selectedOrgan?.name ||
      supervisionOrgans.find((o) => o.id === card?.selectedOrgan?.id)?.name;

    setConfirmModalState({
      open: true,
      cardId,
      organName,
    });
  };

  const handleSaveClick = (cardId: string) => {
    const card = cards.find((c) => c.id === cardId);
    setConfirmModalState({
      open: true,
      cardId,
      organName: card?.selectedOrgan?.name,
    });
  };

  const handleDisconnectClick = (cardId: string) => {
    const card = cards.find((c) => c.id === cardId);
    setDisconnectModalState({
      open: true,
      cardId,
      organName: card?.selectedOrgan?.name,
    });
  };

  const handleConfirmModalOk = async () => {
    if (confirmModalState.cardId) {
      const card = cards.find((c) => c.id === confirmModalState.cardId);
      if (card?.selectedOrgan) {
        await saveCard(confirmModalState.cardId);
      } else {
        await connectCard(confirmModalState.cardId);
      }
      setConfirmModalState({ open: false, cardId: null });
    }
  };

  const handleConfirmModalCancel = () => {
    setConfirmModalState({ open: false, cardId: null });
  };

  const handleDisconnectModalOk = async () => {
    if (disconnectModalState.cardId) {
      await disconnectCard(disconnectModalState.cardId);
      setDisconnectModalState({ open: false, cardId: null });
    }
  };

  const handleDisconnectModalCancel = () => {
    setDisconnectModalState({ open: false, cardId: null });
  };

  const handleOrganChange = (cardId: string, organId: string) => {
    const organ = supervisionOrgans.find((o) => o.id === organId);
    if (organ) {
      updateCard(cardId, { selectedOrgan: organ });
    }
  };

  const handleSwitchChange = (cardId: string, value: boolean) => {
    updateCard(cardId, { buildBusEnabled: value });
  };

  if (!isFlagsLoading && !isIntegrationSettingsEnabled) {
    return <Navigate to={routes.page_not_found()} replace />;
  }

  return (
    <div className="supervision-integration-page">
      <Breadcrumbs routes={breadcrumbs} disableFirst={isUnified ? 3 : 0} />
      <div className="supervision-integration-page__header">
        <Typography.Title level={4}>
          Настройки интеграции
        </Typography.Title>
      </div>
      <div className="supervision-integration-page__warning">
        Будьте внимательны, это повлияет на взаимодействие с органом Надзора!
      </div>

      <Spin spinning={isLoading} tip="Сохранение настроек...">
        <div className="supervision-integration-page__cards">
          {cards.map((card) => (
            <SupervisionIntegrationCard
              key={card.id}
              {...card}
              onConnect={handleConnectClick}
              onSave={handleSaveClick}
              onDisconnect={handleDisconnectClick}
              onEdit={enableEditMode}
              onCopyId={copyId}
              onOrganChange={handleOrganChange}
              onSwitchChange={handleSwitchChange}
              isLoading={isLoading}
            />
          ))}
        </div>
      </Spin>

      <ConfirmIntegrationModal
        open={confirmModalState.open}
        onConfirm={handleConfirmModalOk}
        onCancel={handleConfirmModalCancel}
        cardTitle={confirmModalState.organName}
      />

      <DisconnectIntegrationModal
        open={disconnectModalState.open}
        onConfirm={handleDisconnectModalOk}
        onCancel={handleDisconnectModalCancel}
        organName={disconnectModalState.organName}
      />
    </div>
  );
};
